import React from "react";
import styled from "styled-components";

import { apiGet, apiPut, apiDelete } from "../../../api";
import LeftSidebar from "../../components/left-sidebar";
import { AppLayout, FeedColumn, RightSidebar } from "../../constants/styles";
import TrendingPanel from "../../components/trending-panel";

const PageHeader = styled.div`
  padding: 1rem;
  border-bottom: 1px solid var(--border);
  h1 {
    font-size: 1.25rem;
    font-weight: 700;
  }
`;
const TabBar = styled.div`
  display: flex;
  border-bottom: 1px solid var(--border);
`;
const Tab = styled.button`
  flex: 1;
  padding: 0.75rem;
  background: none;
  border: none;
  cursor: pointer;
  font-weight: ${(p) => (p.$active ? 700 : 400)};
  border-bottom: 2px solid
    ${(p) => (p.$active ? "var(--primary)" : "transparent")};
  color: ${(p) =>
    p.$active ? "var(--foreground)" : "var(--muted-foreground)"};
`;
const Section = styled.div`
  padding: 1rem;
`;
const Label = styled.label`
  display: block;
  font-weight: 600;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
`;
const Input = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  margin-bottom: 1rem;
  font-size: 0.875rem;
`;
const TextArea = styled.textarea`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  resize: vertical;
  min-height: 80px;
`;
const SaveBtn = styled.button`
  padding: 0.5rem 1.5rem;
  background: var(--primary, #ff9900);
  color: #fff;
  border: none;
  border-radius: 20px;
  font-weight: 600;
  cursor: pointer;
`;
const Toggle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--border);
`;
const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;
const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
`;
const UserItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--border);
`;
const UnblockBtn = styled.button`
  padding: 0.25rem 0.75rem;
  border: 1px solid var(--border);
  border-radius: 20px;
  background: none;
  cursor: pointer;
  font-size: 0.8rem;
`;

export default function SettingsPage() {
  const [tab, setTab] = React.useState("profile");
  const [settings, setSettings] = React.useState(null);
  const [profile, setProfile] = React.useState({
    bio: "",
    position: "",
    company: "",
  });
  const [blockedUsers, setBlockedUsers] = React.useState([]);
  const [mutedUsers, setMutedUsers] = React.useState([]);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    apiGet("/api/v1/communities/settings").then((res) => {
      if (res.parsedResponse) {
        setSettings(res.parsedResponse.settings);
        setProfile(res.parsedResponse.profile || {});
      }
    });
    apiGet("/api/v1/communities/blocked_users").then((res) => {
      if (res.parsedResponse?.users) setBlockedUsers(res.parsedResponse.users);
    });
    apiGet("/api/v1/communities/muted_users").then((res) => {
      if (res.parsedResponse?.users) setMutedUsers(res.parsedResponse.users);
    });
  }, []);

  const saveProfile = async () => {
    setSaving(true);
    await apiPut("/api/v1/communities/settings/profile", { body: { profile } });
    setSaving(false);
  };

  const saveSettings = async (updates) => {
    const newSettings = { ...settings, ...updates };
    setSettings(newSettings);
    await apiPut("/api/v1/communities/settings", {
      body: { settings: newSettings },
    });
  };

  const unblock = async (userId) => {
    setBlockedUsers((prev) => prev.filter((u) => u.id !== userId));
    await apiDelete(`/api/v1/communities/users/${userId}/block`);
  };

  const unmute = async (userId) => {
    setMutedUsers((prev) => prev.filter((u) => u.id !== userId));
    await apiDelete(`/api/v1/communities/users/${userId}/mute`);
  };

  if (!settings)
    return (
      <AppLayout>
        <LeftSidebar />
        <FeedColumn>
          <PageHeader>
            <h1>Loading...</h1>
          </PageHeader>
        </FeedColumn>
      </AppLayout>
    );

  return (
    <AppLayout>
      <LeftSidebar activePage="settings" />
      <FeedColumn>
        <PageHeader>
          <h1>Settings</h1>
        </PageHeader>
        <TabBar>
          <Tab $active={tab === "profile"} onClick={() => setTab("profile")}>
            Profile
          </Tab>
          <Tab
            $active={tab === "notifications"}
            onClick={() => setTab("notifications")}
          >
            Notifications
          </Tab>
          <Tab $active={tab === "privacy"} onClick={() => setTab("privacy")}>
            Privacy
          </Tab>
          <Tab $active={tab === "blocked"} onClick={() => setTab("blocked")}>
            Blocked/Muted
          </Tab>
        </TabBar>

        {tab === "profile" && (
          <Section>
            <Label>Bio</Label>
            <TextArea
              value={profile.bio || ""}
              onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
              maxLength={160}
              placeholder="Tell people about yourself"
            />
            <Label>Position</Label>
            <Input
              value={profile.position || ""}
              onChange={(e) =>
                setProfile({ ...profile, position: e.target.value })
              }
              maxLength={100}
              placeholder="e.g. Tax Consultant"
            />
            <Label>Company</Label>
            <Input
              value={profile.company || ""}
              onChange={(e) =>
                setProfile({ ...profile, company: e.target.value })
              }
              maxLength={100}
              placeholder="e.g. Taxsam.co"
            />
            <SaveBtn onClick={saveProfile} disabled={saving}>
              {saving ? "Saving..." : "Save Profile"}
            </SaveBtn>
          </Section>
        )}

        {tab === "notifications" && (
          <Section>
            {[
              { key: "notify_email_dm", label: "Direct messages" },
              { key: "notify_email_mention", label: "Mentions" },
              { key: "notify_email_like", label: "Likes" },
              { key: "notify_email_reply", label: "Replies" },
              { key: "notify_email_follow", label: "New followers" },
              {
                key: "notify_email_digest",
                label: "Daily email digest (unread notifications recap)",
              },
            ].map((item) => (
              <Toggle key={item.key}>
                <span style={{ fontSize: "0.875rem" }}>{item.label}</span>
                <input
                  type="checkbox"
                  checked={settings[item.key] || false}
                  onChange={(e) =>
                    saveSettings({ [item.key]: e.target.checked })
                  }
                />
              </Toggle>
            ))}
          </Section>
        )}

        {tab === "privacy" && (
          <Section>
            <Label>Who can send you direct messages?</Label>
            <RadioGroup>
              {[
                { value: "everyone", label: "Everyone" },
                { value: "followers_only", label: "Only people you follow" },
                { value: "nobody", label: "Nobody" },
              ].map((opt) => (
                <RadioLabel key={opt.value}>
                  <input
                    type="radio"
                    name="dm_privacy"
                    value={opt.value}
                    checked={settings.dm_privacy === opt.value}
                    onChange={() => saveSettings({ dm_privacy: opt.value })}
                  />
                  {opt.label}
                </RadioLabel>
              ))}
            </RadioGroup>
          </Section>
        )}

        {tab === "blocked" && (
          <Section>
            <h3 style={{ fontWeight: 600, marginBottom: "0.5rem" }}>
              Blocked Users
            </h3>
            {blockedUsers.length === 0 ? (
              <p
                style={{
                  color: "var(--muted-foreground)",
                  fontSize: "0.875rem",
                }}
              >
                No blocked users
              </p>
            ) : (
              blockedUsers.map((u) => (
                <UserItem key={u.id}>
                  <span>
                    {u.name}{" "}
                    <span style={{ color: "var(--muted-foreground)" }}>
                      {u.handle}
                    </span>
                  </span>
                  <UnblockBtn onClick={() => unblock(u.id)}>Unblock</UnblockBtn>
                </UserItem>
              ))
            )}
            <h3
              style={{
                fontWeight: 600,
                marginTop: "1.5rem",
                marginBottom: "0.5rem",
              }}
            >
              Muted Users
            </h3>
            {mutedUsers.length === 0 ? (
              <p
                style={{
                  color: "var(--muted-foreground)",
                  fontSize: "0.875rem",
                }}
              >
                No muted users
              </p>
            ) : (
              mutedUsers.map((u) => (
                <UserItem key={u.id}>
                  <span>
                    {u.name}{" "}
                    <span style={{ color: "var(--muted-foreground)" }}>
                      {u.handle}
                    </span>
                  </span>
                  <UnblockBtn onClick={() => unmute(u.id)}>Unmute</UnblockBtn>
                </UserItem>
              ))
            )}
          </Section>
        )}
      </FeedColumn>
      <RightSidebar>
        <TrendingPanel />
      </RightSidebar>
    </AppLayout>
  );
}
